import {
  EmptyState,
  ErrorState,
  LoadingState,
  type Locale,
  WithHalfdot,
} from "@elyxeer/ui";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { StatusChip } from "~/components/articles/StatusChip";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { fetchAdminArticles } from "~/lib/api";
import { articleStatus } from "~/lib/article-status";
import { queryKeys } from "~/lib/query-client";
import type { NewsArticleSort, SortOrder } from "~/types/news-admin";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const TABLE_ICON_SIZE = 20;

/** Every article — drafts and scheduled posts included — newest first. */
export function ArticlesRoute() {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  // undefined = both languages; the table's locale column keeps the rows tellable apart.
  const [filter, setFilter] = useState<Locale | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<NewsArticleSort>("published_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const query = useInfiniteQuery({
    queryKey: queryKeys.articles({
      locale: filter,
      search,
      sortBy,
      sortOrder,
    }),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminArticles({
        locale: filter,
        search,
        sortBy,
        sortOrder,
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const articles = query.data?.pages.flatMap((page) => page.items) ?? [];

  const dateFormat = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  const changeSort = (column: NewsArticleSort) => {
    if (sortBy === column) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    // Dates are normally useful newest-first; words and state read naturally A–Z.
    setSortOrder(
      column === "published_at" || column === "updated_at" ? "desc" : "asc",
    );
  };

  return (
    <Screen maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("articles.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("articles.subtitle")}
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/articles/new"
          variant="contained"
          startIcon={<AddIcon />}
          data-testid="articles.new"
        >
          {t("articles.newArticle")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        <Tabs
          value={filter ?? "all"}
          onChange={(_event, value: string) =>
            setFilter(value === "all" ? undefined : (value as Locale))
          }
          sx={{ flex: 1, minWidth: 300 }}
        >
          <Tab label={t("articles.filter.all")} value="all" />
          <Tab label={t("articles.filter.cs")} value="cs" />
          <Tab label={t("articles.filter.en")} value="en" />
        </Tabs>

        <TextField
          label={t("articles.search")}
          type="search"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: TABLE_ICON_SIZE }} />
                </InputAdornment>
              ),
            },
            htmlInput: { "data-testid": "articles.search" },
          }}
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
      </Box>

      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : articles.length === 0 ? (
        <EmptyState
          message={t(search ? "articles.emptySearch" : "articles.empty")}
        />
      ) : (
        <>
          {/* The table scrolls inside its own container; the page never scrolls sideways. */}
          <Box sx={{ overflowX: "auto" }}>
            <Table data-testid="articles.table" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sortDirection={sortBy === "title" ? sortOrder : false}
                    sx={{ width: "34%" }}
                  >
                    <TableSortLabel
                      active={sortBy === "title"}
                      direction={sortBy === "title" ? sortOrder : "asc"}
                      onClick={() => changeSort("title")}
                      data-testid="articles.sort-title"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("articles.columns.title")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "locale" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "locale"}
                      direction={sortBy === "locale" ? sortOrder : "asc"}
                      onClick={() => changeSort("locale")}
                      data-testid="articles.sort-locale"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("articles.columns.locale")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "format" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "format"}
                      direction={sortBy === "format" ? sortOrder : "asc"}
                      onClick={() => changeSort("format")}
                      data-testid="articles.sort-format"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("articles.columns.format")}
                    </TableSortLabel>
                  </TableCell>
                  {/* Not sortable: an article carries up to three topics, so there
                      is no single value for the server to order rows by. */}
                  <TableCell>{t("articles.columns.topics")}</TableCell>
                  <TableCell
                    sortDirection={sortBy === "status" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "status"}
                      direction={sortBy === "status" ? sortOrder : "asc"}
                      onClick={() => changeSort("status")}
                      data-testid="articles.sort-status"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("articles.columns.status")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={
                      sortBy === "published_at" ? sortOrder : false
                    }
                  >
                    <TableSortLabel
                      active={sortBy === "published_at"}
                      direction={sortBy === "published_at" ? sortOrder : "desc"}
                      onClick={() => changeSort("published_at")}
                      data-testid="articles.sort-published-at"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("articles.columns.publishedAt")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "updated_at" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "updated_at"}
                      direction={sortBy === "updated_at" ? sortOrder : "desc"}
                      onClick={() => changeSort("updated_at")}
                      data-testid="articles.sort-updated-at"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("articles.columns.updatedAt")}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {articles.map((article) => (
                  <TableRow
                    key={article.id}
                    hover
                    onClick={() => void navigate(`/articles/${article.id}`)}
                    data-testid={`articles.row-${article.locale}-${article.slug}`}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="L1" sx={{ color: "text.primary" }}>
                        {article.title}
                      </Typography>
                      <Typography
                        variant="T3"
                        component="p"
                        sx={{ color: "text.muted" }}
                      >
                        {article.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={article.locale}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`formats.${article.format}`)}
                        size="small"
                        variant="outlined"
                        data-testid={`articles.format-${article.slug}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        data-testid={`articles.topics-${article.slug}`}
                      >
                        {article.topics.map((topic) => (
                          <Chip
                            key={topic}
                            label={t(`topics.${topic}`)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={articleStatus(article)} />
                    </TableCell>
                    <TableCell>
                      <time dateTime={article.published_at}>
                        {formatDate(article.published_at, dateFormat)}
                      </time>
                    </TableCell>
                    <TableCell>
                      <time dateTime={article.updated_at}>
                        {formatDate(article.updated_at, dateFormat)}
                      </time>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {query.hasNextPage ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <Button
                variant="outlined"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                data-testid="articles.load-more"
              >
                {t("articles.loadMore")}
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </Screen>
  );
}
